import React from 'react'

import { PRODUCT_CARD_CLICKED } from '@/constants/product-events'
import type { ProductCardProps } from '@/types/components/cards/product-card'
import { Tag } from '@/components/Tag'

import './Base.scss'
import { formatPrice } from '@/utilities/number'
import { ResponsiveImage } from '@/components/Shared/ResponsiveImage'
import { AddToCartButton } from './AddToCartButton'
import {
  isProductCardVariantEnabled,
  variantIsAvailableForSale,
} from '@/utilities/commerce'
import type { CMSCollectionPage, CMSProductCardVariant } from '@/types/sanity'
import { RatingStars } from '@/components/BuyingPanel/RatingStars'
import { TRACKING_TIMEOUT } from '@/scripts/tracking/chord'
import type { Bottomline } from '@/types/yotpo'
import { delay } from '@/utilities/timing'

type Props = ProductCardProps & {
  cta: CMSCollectionPage['cta']
  backgroundColor: string
  bottomline?: Bottomline
  selectedVariant?: CMSProductCardVariant
}

export const ProductCardV2: React.FC<Props> = ({
  content,
  cta,
  backgroundColor,
  bottomline,
  selectedVariant,
}) => {
  const {
    slug,
    activeVariant,
    tag,
    title,
    subtitle,
    variants,
    productImage,
    alternativeImage,
    showReviews,
  } = content

  const enabledVariants = variants.filter(isProductCardVariantEnabled)
  if (!enabledVariants.length) return null

  const handleProductCardClick: React.MouseEventHandler<
    HTMLAnchorElement
  > = async (event) => {
    event.preventDefault()
    window.dispatchEvent(
      new CustomEvent(PRODUCT_CARD_CLICKED, { detail: content }),
    )
    await delay(TRACKING_TIMEOUT)
    window.location.href = productHref
  }

  const isSingleVariant = enabledVariants.length === 1
  const isInStock = enabledVariants.some(variantIsAvailableForSale)

  const productHref = `/products/${slug}?variant=${activeVariant.id}`
  const price =
    selectedVariant?.price ||
    Math.min(...enabledVariants.map((variant) => variant.price))
  const compareAtPrice =
    selectedVariant?.compareAtPrice ||
    Math.min(...enabledVariants.map((variant) => variant.compareAtPrice || 0))

  const style = {
    '--background-color': backgroundColor,
  } as React.CSSProperties

  return (
    <div className="product-card" style={style}>
      <a href={productHref} onClick={handleProductCardClick}>
        <div className="product-card__image-wrapper">
          <div className="product-card__tag">
            {isInStock ? (
              tag && <Tag type="compact" theme={tag.theme} text={tag.label} />
            ) : (
              <Tag type="compact" theme="grey" text="Sold Out" />
            )}
          </div>
          <div className="product-card__cover-image">
            {alternativeImage && (
              <ResponsiveImage
                document={alternativeImage}
                overrides={{
                  objectFit: 'cover',
                  desktopAspectRatio: '3/4',
                  mobileAspectRatio: '3/4',
                  customSizes: [640, 768, 1024, 1200],
                }}
              />
            )}
          </div>
          <div className="product-card__image">
            <ResponsiveImage
              document={productImage}
              overrides={{
                objectFit: 'contain',
                desktopAspectRatio: '3/4',
                mobileAspectRatio: '3/4',
                customSizes: [640, 768, 1024, 1200],
              }}
            />
          </div>
        </div>
      </a>
      <div className="product-card__content">
        <a
          className="product-card__details"
          href={productHref}
          onClick={handleProductCardClick}
        >
          <div className="product-card__header">
            <div className="product-card__title type-label">{title}</div>
            {subtitle && <div>{subtitle}</div>}
            {showReviews && (
              <div className="product-card__rating">
                <RatingStars
                  rating={bottomline?.average_score || 0}
                  showAverageScore={true}
                />
              </div>
            )}
          </div>
          <div className="product-card__price type-label">
            {!!compareAtPrice && (
              <span className="product-card__compare-price">
                {formatPrice(compareAtPrice)}
              </span>
            )}
            <span className="product-card__actual-price type-label">
              {isSingleVariant || selectedVariant
                ? formatPrice(price)
                : `From ${formatPrice(price)}`}
            </span>
          </div>
        </a>
        {cta?.isVisible && (
          <AddToCartButton
            variantCardItem={selectedVariant}
            productCardItem={content}
            buttonType={cta.type || 'primary'}
            classes={['product-card__cta']}
          />
        )}
      </div>
    </div>
  )
}
